import type { NuxtApp } from '#app';
import { setErrorReporter, formatAppError } from '@/shared/lib/errors/logger';
import type { AppError } from '@/shared/model/types/error';

type SentryClient = typeof import('@sentry/nuxt');
type CaptureArgs = Parameters<SentryClient['captureException']>;

const isIgnoredNuxtError = (error: unknown): boolean =>
  isNuxtError(error) && Number(error.statusCode) >= 300 && Number(error.statusCode) < 500;

export default defineNuxtPlugin({
  name: 'sentry-lazy',
  setup(nuxtApp: NuxtApp) {
    const dsn = String(useRuntimeConfig().public.sentryDsn ?? '');
    if (!dsn) return;

    let captureLoaded: SentryClient['captureException'] | null = null;
    const pending: CaptureArgs[] = [];

    const capture = (...args: CaptureArgs): void => {
      if (captureLoaded) {
        captureLoaded(...args);
        return;
      }
      pending.push(args);
    };

    const onWindowError = (event: ErrorEvent): void => {
      capture(event.error ?? new Error(event.message));
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
      capture(event.reason);
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    const load = async (): Promise<void> => {
      const { init, captureException } = await import('@sentry/nuxt');

      init({
        dsn,
        environment: import.meta.dev ? 'development' : 'production',
        integrations: (defaults) =>
          defaults.filter((integration) => integration.name !== 'BrowserSession'),
      });

      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);

      captureLoaded = captureException;
      pending.splice(0).forEach((args) => captureException(...args));
    };

    onNuxtReady(() => {
      load().catch((error) => console.error('[sentry] init failed', error));
    });

    nuxtApp.hook('vue:error', (error, _instance, info) => {
      capture(error, { extra: { info } });
    });

    nuxtApp.hook('app:error', (error) => {
      if (isIgnoredNuxtError(error)) return;
      capture(error);
    });

    setErrorReporter((error: AppError, context) => {
      const exception =
        error.cause instanceof Error ? error.cause : new Error(formatAppError(error));

      capture(exception, {
        tags: { errorKind: error.kind, status: String(error.status) },
        extra: {
          appMessage: error.message,
          fields: error.fields,
          ...(error.cause instanceof Error ? {} : { cause: error.cause }),
          context,
        },
      });
    });
  },
});
